import styled, { css } from 'styled-components';
import {
    BorderRadiusMedium,
    FontBody1LineHeight,
    FontBody2WeightRegular,
    FontCaptionFamily,
    FontCaptionWeightSemiBold,
    FontSize12,
    Spacing4
} from 'autogen/design-tokens/tokens';
import type { TopBannerModel } from 'autogen/swagger/all';
import FlexColumn from 'components/Layout/components/Flex/components/FlexColumn';
import FlexRow from 'components/Layout/components/Flex/components/FlexRow';
import Disclaimer from 'components/Streamers/disclaimer';
import PageAction from 'components/shared/Routing/PageAction';
import format from 'helpers/translations';
import ThemeProvider from 'theming/ThemeProvider';
import { breakpoint, sizes } from 'variables';

export const StreamerWrapper = styled.div<{ singleBanner: boolean }>`
    color: ${({ theme }) => theme.ColorTextPrimary};
`;

const streamerInnerStyles = css`
    && {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: ${BorderRadiusMedium};
        height: 40px;
        text-decoration: none !important;
        background-color: ${({ theme }) => theme.ColorBackgroundPrimary};
        width: 100%;

        ${breakpoint.up('1025px')} {
            flex-direction: column;
            height: 48px;
        }
    }
`;

const StreamerInnerPageAction = styled(PageAction)`
    ${streamerInnerStyles}
`;

const StreamerInnerStatic = styled.div`
    ${streamerInnerStyles}
`;

const Text = styled.div<{ type: TopBannerModel['type'] }>`
    font-size: ${FontSize12};
    font-weight: ${FontBody2WeightRegular};
    line-height: ${FontBody1LineHeight};
    text-align: center;

    & b {
        font-weight: ${FontCaptionWeightSemiBold};
    }
`;

const MobileText = styled(Text)`
    display: flex;

    ${breakpoint.up(sizes.xl)} {
        display: none;
    }
`;

const DesktopText = styled(Text)`
    display: none;

    ${breakpoint.up(sizes.xl)} {
        display: flex;
        align-items: center;
    }
`;

const CallToActionText = styled.span<{ type: TopBannerModel['type'] }>`
    display: none;
    font-family: ${FontCaptionFamily};
    font-weight: ${FontCaptionWeightSemiBold};
    font-size: ${FontSize12};
    text-decoration: underline;

    //Desktop
    ${breakpoint.up('1025px')} {
        display: block;
    }
`;

const StyledFlexColumn = styled(FlexColumn)`
    padding: 0 ${({ theme }) => theme.Spacing8};
`;

interface ITopBannerModel extends TopBannerModel {
    singleBanner?: boolean;
}

function StreamerItem(props: ITopBannerModel): JSX.Element {
    const {
        dataLayerDto,
        disclaimer,
        ctaText,
        pageAction,
        mobileText,
        text,
        type,
        designThemeName,
        singleBanner = false,
        ...restProps
    } = props;

    const content = (
        <StyledFlexColumn gap="0" alignItems="center">
            <FlexRow gap={Spacing4} alignItems="center">
                <MobileText type={type}>{format(mobileText)}</MobileText>
                <DesktopText type={type}>{format(text)}</DesktopText>
                {disclaimer && <Disclaimer disclaimer={disclaimer} />}
            </FlexRow>
            {ctaText && <CallToActionText type={type}>{ctaText}</CallToActionText>}
        </StyledFlexColumn>
    );

    return (
        <ThemeProvider theme={designThemeName}>
            <StreamerWrapper singleBanner={singleBanner} data-datalayer={JSON.stringify(dataLayerDto)} {...restProps}>
                {pageAction ? (
                    <StreamerInnerPageAction action={pageAction}>{content}</StreamerInnerPageAction>
                ) : (
                    <StreamerInnerStatic>{content}</StreamerInnerStatic>
                )}
            </StreamerWrapper>
        </ThemeProvider>
    );
}

export default StreamerItem;
